// "From PLN-…": the planned order a document was converted from (spec 03
// §3.4, spec 04 §4 S2, spec 06 §3.3; D-396 rulings 12(e) and 23). Module 04's
// S2 and module 06's transfer detail both carry it, so it lives here, beside
// the drawer it opens.
//
// It is shown only to a reader the planning mirror resolves to
// `planning.planned_order.read`, and links to module 03's drawer
// (`/planning/orders?order=<id>`). The number is read from module 03's own
// detail route; while it loads, and when the read is refused, the line says
// "From a planned order" without a link rather than a bare id.
//
// THE READ IS NOT ASKED WHEN IT IS KNOWN TO FAIL. The server serves a planned
// order only to a reader of `planning.planned_order.read` at its facility, and
// a procurement reader only its purchase orders (spec 03 §7 narrowing (c)).
// A transfer drafted from a planned order belongs to the RECEIVING plant's
// MRP, so a storekeeper or a planner at the sending plant opened the transfer
// and fired a 404 for an order they may not read. The line now asks only when
// the order's facility is in the reader's facility list and its kind is one
// their role is served; otherwise it says "From a planned order" at once.

import { Link } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { usePlannedOrder, type PlannedOrderKind } from "../hooks/mrp-queries";
import { usePlanningPermissions } from "../hooks/permissions";
import { usePlanningFacilities } from "../hooks/planning-queries";

/**
 * Whether the planned-order read can succeed for this reader: "yes", "no", or
 * "unknown" while the facility list is in flight. A facility list that failed
 * says nothing, so the read is asked and the server decides.
 */
export function plannedOrderReadable(input: {
  everyKind: boolean;
  facilityIds: readonly string[] | undefined;
  facilitiesFailed: boolean;
  facilityId: string;
  kind: PlannedOrderKind;
}): "yes" | "no" | "unknown" {
  if (!input.everyKind && input.kind !== "purchase") return "no";
  if (input.facilitiesFailed) return "yes";
  if (input.facilityIds === undefined) return "unknown";
  return input.facilityIds.includes(input.facilityId) ? "yes" : "no";
}

/** Whether this reader is shown where a document came from. */
export function useCanSeePlannedOrigin(): boolean {
  return usePlanningPermissions().has("planning.planned_order.read");
}

export function PlannedOrderRef({
  plannedOrderId,
  testId,
  facilityId,
  kind,
}: {
  plannedOrderId: string;
  testId: string;
  /** The planned order's facility: a work order's own, a transfer's receiving plant. */
  facilityId: string;
  kind: PlannedOrderKind;
}) {
  const allowed = useCanSeePlannedOrigin();
  if (!allowed) return null;
  return (
    <PlannedOrderRefLoaded
      plannedOrderId={plannedOrderId}
      testId={testId}
      facilityId={facilityId}
      kind={kind}
    />
  );
}

function PlannedOrderRefLoaded({
  plannedOrderId,
  testId,
  facilityId,
  kind,
}: {
  plannedOrderId: string;
  testId: string;
  facilityId: string;
  kind: PlannedOrderKind;
}) {
  const { t } = useTranslation();
  const perms = usePlanningPermissions();
  const facilities = usePlanningFacilities();
  const readable = plannedOrderReadable({
    everyKind: perms.readsEveryPlannedOrderKind,
    facilityIds: facilities.data?.items.map((f) => f.id),
    facilitiesFailed: facilities.error !== null,
    facilityId,
    kind,
  });
  const detail = usePlannedOrder(readable === "yes" ? plannedOrderId : undefined);
  const poNo = readable === "yes" ? detail.data?.order.po_no : undefined;
  if (poNo === undefined) {
    return (
      <span data-testid={testId} data-linked="false">
        {t("planning.origin.fromPlannedOrder")}
      </span>
    );
  }
  return (
    <Link
      to="/planning/orders"
      search={{ order: plannedOrderId }}
      data-testid={testId}
      data-linked="true"
      className="ds-lnk"
    >
      {t("planning.origin.fromPlannedOrderNo", { number: poNo })}
    </Link>
  );
}
